import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Send, CheckCheck, Eye, XCircle, MessageCircle, User, Reply } from "lucide-react";
import api from "../../services/api";

const STATUS_CONFIG = {
  sent:      { label: "Sent",       icon: Send,       color: "text-gray-500",   activeColor: "text-gray-700",   activeBg: "bg-gray-100",  dot: "bg-gray-400",   bar: "bg-gray-300"   },
  delivered: { label: "Unread",     icon: CheckCheck, color: "text-orange-400", activeColor: "text-orange-500", activeBg: "bg-orange-50", dot: "bg-orange-400", bar: "bg-orange-200" },
  read:      { label: "Read",       icon: Eye,        color: "text-purple-400", activeColor: "text-purple-600", activeBg: "bg-purple-50", dot: "bg-purple-400", bar: "bg-purple-200" },
  replied:   { label: "Replied by", icon: Reply,      color: "text-teal-400",   activeColor: "text-teal-600",   activeBg: "bg-teal-50",   dot: "bg-teal-400",   bar: "bg-teal-200"   },
  failed:    { label: "Failed",     icon: XCircle,    color: "text-red-400",    activeColor: "text-red-500",    activeBg: "bg-red-50",    dot: "bg-red-400",    bar: "bg-red-200"    },
};

const AVATAR_COLORS = [
  "bg-teal-500", "bg-blue-500", "bg-purple-500", "bg-pink-500",
  "bg-orange-500", "bg-green-500", "bg-indigo-500", "bg-rose-500",
];

const getAvatarColor = (str = "") => {
  let h = 0;
  for (let i = 0; i < str.length; i++) h = str.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};

const BroadcastContactsSidebar = ({ broadcastId, initialStatus, onClose }) => {
  const navigate = useNavigate();
  const [activeStatus, setActiveStatus] = useState(initialStatus);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!broadcastId || !activeStatus) return;
    setLoading(true);
    setContacts([]);
    api
      .get(`/broadcasts/${broadcastId}/contacts?status=${activeStatus}`)
      .then((r) => setContacts(r.data.data || []))
      .catch(() => setContacts([]))
      .finally(() => setLoading(false));
  }, [broadcastId, activeStatus]);

  const cfg = STATUS_CONFIG[activeStatus] || STATUS_CONFIG.sent;
  const Icon = cfg.icon;

  return (
    <>
      {/* Backdrop — only covers the content area below the header */}
      <div
        className="fixed top-16 bottom-0 left-0 right-0 z-40 bg-black/20 backdrop-blur-[1px]"
        onClick={onClose}
      />

      {/* Sidebar panel */}
      <div
        className="fixed top-16 bottom-0 right-0 z-50 w-80 flex flex-col bg-white shadow-2xl border-l border-gray-200"
        style={{ animation: "slideInRight 0.22s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3.5 border-b border-gray-100 flex-shrink-0 bg-white">
          <div className="flex items-center gap-2.5">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${cfg.activeBg}`}>
              <Icon className={`w-3.5 h-3.5 ${cfg.activeColor}`} />
            </div>
            <div>
              <p className="text-[13px] font-semibold text-gray-800 leading-tight">
                {cfg.label} Recipients
              </p>
              {!loading && (
                <p className="text-[11px] text-gray-400 leading-tight">
                  {contacts.length} contact{contacts.length !== 1 ? "s" : ""}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Status tabs */}
        <div className="flex flex-shrink-0 px-3 pt-2.5 pb-0 gap-1">
          {Object.entries(STATUS_CONFIG).map(([key, val]) => {
            const TabIcon = val.icon;
            const isActive = activeStatus === key;
            return (
              <button
                key={key}
                onClick={() => setActiveStatus(key)}
                className={`flex-1 flex flex-col items-center gap-0.5 py-2 px-1 rounded-xl text-[10px] font-semibold transition-all ${
                  isActive
                    ? `${val.activeBg} ${val.activeColor} shadow-sm`
                    : "text-gray-400 hover:text-gray-600 hover:bg-gray-50"
                }`}
              >
                <TabIcon className={`w-3.5 h-3.5 ${isActive ? val.activeColor : ""}`} />
                {val.label}
              </button>
            );
          })}
        </div>

        {/* Divider */}
        <div className="mx-3 mt-2.5 mb-0 h-px bg-gray-100 flex-shrink-0" />

        {/* Contact list */}
        <div className="flex-1 overflow-y-auto py-2">
          {loading && (
            <div className="flex flex-col items-center justify-center py-12 gap-2">
              <div className="w-6 h-6 border-2 border-gray-200 border-t-gray-400 rounded-full animate-spin" />
              <p className="text-xs text-gray-400">Loading contacts…</p>
            </div>
          )}

          {!loading && contacts.length === 0 && (
            <div className="flex flex-col items-center justify-center py-12 gap-3 px-4 text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${cfg.activeBg}`}>
                <Icon className={`w-6 h-6 ${cfg.activeColor} opacity-60`} />
              </div>
              <p className="text-[13px] font-medium text-gray-500">
                No {cfg.label.toLowerCase()} recipients
              </p>
              <p className="text-[11px] text-gray-400 leading-relaxed">
                Contacts in this category will appear here.
              </p>
            </div>
          )}

          {!loading &&
            contacts.map((c, i) => {
              const name = c.Customer?.name || c.Customer?.phoneNumber || "Unknown";
              const phone = c.Customer?.phoneNumber || "—";
              const initial = (name[0] || "?").toUpperCase();
              const avatarColor = getAvatarColor(name);

              return (
                <div
                  key={c.id}
                  className="flex items-center gap-3 px-3 py-2.5 mx-2 rounded-xl hover:bg-gray-50 transition-colors group"
                >
                  {/* Avatar */}
                  <div
                    className={`w-9 h-9 rounded-full ${avatarColor} flex items-center justify-center flex-shrink-0`}
                  >
                    <span className="text-[13px] font-bold text-white">{initial}</span>
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-medium text-gray-800 truncate leading-tight">
                      {name}
                    </p>
                    <p className="text-[11px] text-gray-400 font-mono leading-tight mt-0.5">
                      +{phone.replace(/^\+/, "")}
                    </p>
                  </div>

                  {/* Status dot */}
                  <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${cfg.dot} group-hover:hidden`} />

                  {/* Chat button — shown on hover */}
                  <button
                    onClick={() => navigate("/chats", { state: { openPhone: phone } })}
                    className="hidden group-hover:flex items-center gap-1 text-[11px] text-[#075e54] hover:text-[#064e3b] font-medium flex-shrink-0 bg-[#075e54]/10 hover:bg-[#075e54]/20 px-2 py-1 rounded-lg transition-all"
                    title="Open chat"
                  >
                    <MessageCircle className="w-3 h-3" />
                    Chat
                  </button>
                </div>
              );
            })}
        </div>

        {/* Footer count bar */}
        {!loading && contacts.length > 0 && (
          <div className="flex-shrink-0 px-4 py-2.5 border-t border-gray-100 bg-gray-50/80">
            <p className="text-[11px] text-gray-400 text-center">
              Showing {contacts.length} {cfg.label.toLowerCase()} contact{contacts.length !== 1 ? "s" : ""}
            </p>
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideInRight {
          from { transform: translateX(100%); opacity: 0.6; }
          to   { transform: translateX(0);    opacity: 1;   }
        }
      `}</style>
    </>
  );
};

export default BroadcastContactsSidebar;
